import { useCallback, useEffect, useState } from 'react';
import { useMyDIDs } from '../../api/hooks';
import { upi, rupees, toPaise, type Mandate, type PaymentResult } from '../../api/upi';
import { findKeyInDID, importEd25519Key, parseEd25519PrivateKey } from '../../lib/credentialSigning';
import UPIStatusBadge from './UPIStatusBadge';
import { shortDid } from './terms';

interface Props {
  /** The agent that pays. */
  agentDid: string;
  /** Called after a payment is submitted, so the list can reload. */
  onSubmitted: () => void;
}

const input = 'mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs';

const b64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const b64urlJSON = (v: unknown) => b64url(new TextEncoder().encode(JSON.stringify(v)));

/**
 * For QA and demos on the mock bank: plays the agent, which signs a payment
 * token with its key in this browser, and the merchant, which submits it.
 * In production the agent signs on its operator's servers and the merchant's
 * checkout submits the token.
 */
export default function TestCheckout({ agentDid, onSubmitted }: Props) {
  const { data: dids } = useMyDIDs();
  const [mandates, setMandates] = useState<Mandate[]>([]);
  const [mandateId, setMandateId] = useState('');
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [merchant, setMerchant] = useState('');
  const [keyText, setKeyText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PaymentResult | null>(null);
  const [error, setError] = useState('');

  // Reloaded after each payment so the spend shown stays current.
  const loadMandates = useCallback((pickFirst: boolean) => {
    upi.mandates({ agent: agentDid })
      .then((all) => {
        const active = all.filter((m) => m.status === 'active');
        setMandates(active);
        if (pickFirst && active[0]) choose(active[0]);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    // choose only sets form fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentDid]);
  useEffect(() => { loadMandates(true); }, [loadMandates]);

  // The merchant must be a DID the caller controls; an agent is not a merchant.
  const merchants = (dids ?? []).filter((d) => d.status === 'active' && !d.did.startsWith('did:adi:agent:'));
  useEffect(() => { if (!merchant && merchants[0]) setMerchant(merchants[0].did); }, [merchant, merchants]);

  const choose = (m: Mandate) => {
    setMandateId(m.id);
    const allowed = m.constraints.allowedMerchantVPAs;
    setPayee(Array.isArray(allowed) && allowed.length > 0 ? String(allowed[0]) : 'freshmart@examplebank');
  };

  const pay = async () => {
    setError('');
    setResult(null);
    const amt = toPaise(amount);
    if (amt === null || amt <= 0) return setError('Enter an amount in rupees, with at most two decimals.');
    setBusy(true);
    try {
      const key = await importEd25519Key(parseEd25519PrivateKey(keyText));
      const kid = await findKeyInDID(agentDid, key.publicKey, 'authentication');
      const now = Math.floor(Date.now() / 1000);
      const signingInput = `${b64urlJSON({ alg: 'EdDSA', typ: 'ibct+jwt', kid })}.${b64urlJSON({
        iss: agentDid, aud: merchant, mandate: mandateId, act: 'upi-pay', amt, payee: payee.trim(),
        jti: crypto.randomUUID(), iat: now, exp: now + 60,
      })}`;
      const sig = new Uint8Array(await crypto.subtle.sign({ name: 'Ed25519' }, key.key, new TextEncoder().encode(signingInput)));
      setResult(await upi.submitPayment({ token: `${signingInput}.${b64url(sig)}`, merchantDid: merchant, merchantOrderId: `TEST-${crypto.randomUUID().slice(0, 6)}` }));
      onSubmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      loadMandates(false);
    }
  };

  const selected = mandates.find((m) => m.id === mandateId);

  return (
    <section className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
      <h3 className="text-sm font-bold text-text-main">Test checkout</h3>
      <p className="mt-1 text-xs text-text-muted">
        For testing on the mock bank. This form plays the agent, which signs the payment with its key in this browser, and the merchant,
        which submits it. In production the agent signs on its operator's servers and the merchant's checkout submits the payment.
      </p>

      {mandates.length === 0 ? (
        <p className="mt-4 text-xs text-text-muted">This agent has no active mandate. A customer has to sign one first.</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
            <label className="block sm:col-span-4">
              <span className="text-xs font-semibold text-text-muted">Mandate</span>
              <select id="checkout-mandate" value={mandateId} onChange={(e) => { const m = mandates.find((x) => x.id === e.target.value); if (m) choose(m); }} className={input}>
                {mandates.map((m) => (
                  <option key={m.id} value={m.id}>{m.consent?.purpose || 'Payment mandate'} · customer {shortDid(m.customerDid)}</option>
                ))}
              </select>
              {selected?.spend && selected.constraints.maxPerDayPaise !== undefined && (
                <span className="mt-1 block text-[11px] text-text-faint">
                  Up to {rupees(selected.constraints.maxPerTxnPaise)} a payment; {rupees(selected.spend.daySpentPaise)} of {rupees(selected.constraints.maxPerDayPaise)} spent today
                </span>
              )}
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-text-muted">Amount (₹)</span>
              <input id="checkout-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="849" className={input} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-text-muted">Pay to (VPA)</span>
              <input id="checkout-payee" value={payee} onChange={(e) => setPayee(e.target.value)} className={`${input} font-mono`} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold text-text-muted">Merchant (one of your DIDs)</span>
              <select id="checkout-merchant" value={merchant} onChange={(e) => setMerchant(e.target.value)} className={`${input} font-mono`}>
                {merchants.length === 0 && <option value="">Create a DID to act as the merchant</option>}
                {merchants.map((d) => <option key={d.did} value={d.did}>{d.did}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-4">
              <span className="text-xs font-semibold text-text-muted">Agent private key (shown once when the agent was registered)</span>
              <input id="checkout-key" type="password" autoComplete="off" spellCheck={false} value={keyText} onChange={(e) => setKeyText(e.target.value)} placeholder="Hex or base64; it stays in this browser" className={`${input} font-mono`} />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button id="checkout-pay" onClick={() => void pay()} disabled={busy || !amount.trim() || !keyText.trim() || !merchant || !payee.trim()} className="btn-primary disabled:opacity-50">
              {busy ? 'Paying…' : 'Sign and pay'}
            </button>
          </div>
        </>
      )}

      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{error}</div>}
      {result && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border-subtle bg-slate-50 p-3 text-xs">
          <UPIStatusBadge status={result.status} />
          <span className="text-text-muted">
            {result.status === 'PENDING_HUMAN' ? 'The customer has to approve this payment.' : result.rrn ? <>RRN <span className="font-mono text-text-main">{result.rrn}</span></> : result.reasonCode ?? ''}
          </span>
        </div>
      )}
    </section>
  );
}
